import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize from localStorage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('osyla_cart');
      console.log('🔍 CartContext INIT - Loading from localStorage:', savedCart);
      if (!savedCart || savedCart === 'null' || savedCart === 'undefined') {
        console.log('🔍 CartContext INIT - No valid cart found, starting with empty array');
        return [];
      }
      const parsed = JSON.parse(savedCart);
      console.log('🔍 CartContext INIT - Parsed cart items:', Array.isArray(parsed) ? parsed.length : 0, 'items');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('❌ Error loading cart from localStorage:', error);
      return [];
    }
  });
  
  // Add a ref to track if we've initialized
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Save to localStorage whenever cartItems changes (but not on first render)
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }
    
    try {
      console.log('💾 CartContext - Saving to localStorage:', cartItems.length, 'items');
      localStorage.setItem('osyla_cart', JSON.stringify(cartItems));
      console.log('✅ Cart saved successfully');
    } catch (error) {
      console.error('❌ Error saving cart to localStorage:', error);
    }
  }, [cartItems, isInitialized]);

  const addToCart = (item) => {
    setCartItems(prevItems => {
      // Check if item already exists
      const existingItemIndex = prevItems.findIndex(
        cartItem => cartItem.product.id === item.product.id
      );

      if (existingItemIndex > -1) {
        // Update quantity if exists
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].configuration.quantity += item.configuration.quantity;
        updatedItems[existingItemIndex].totalPrice += item.totalPrice;
        return updatedItems;
      }

      // Add new item
      return [...prevItems, item];
    });
  };

  const removeFromCart = (index) => {
    setCartItems(prevItems => prevItems.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    
    setCartItems(prevItems => {
      const updatedItems = [...prevItems];
      const item = updatedItems[index];
      const pricePerUnit = item.totalPrice / item.configuration.quantity;
      
      updatedItems[index] = {
        ...item,
        configuration: {
          ...item.configuration,
          quantity: newQuantity
        },
        totalPrice: pricePerUnit * newQuantity
      };
      
      return updatedItems;
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.totalPrice, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.configuration.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
